import {
  ActionIcon,
  Box,
  Button,
  Grid,
  Group,
  Modal,
  NavLink,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, izvedi } from '../api/odjemalec';
import { sporocilo } from '../api/napaka';
import { preteklo } from '../oblika/oblika';
import { GlavaZaslona } from '../seznam/Seznam';
import { KLJUC_ASISTENT, usePogovori, type Pogovor } from './asistent';
import { PogovorAsistenta } from './PogovorAsistenta';

/**
 * Asistent čez cel zaslon (/asistent, /asistent/:id): levo pogovori uporabnika (na telefonu izbira nad pogovorom),
 * desno izbrani pogovor ali nov. Isti pogovor kot v predalu iz glave.
 */
export function Asistent() {
  const { id } = useParams();
  const pogovorId = id ? Number(id) : null;
  const navigate = useNavigate();
  const pogovori = usePogovori();
  const seznam = pogovori.data?.vsebina ?? [];
  const [izbris, setIzbris] = useState<Pogovor | null>(null);

  return (
    <Box maw={1400} mx="auto">
      <GlavaZaslona
        naslov="Asistent"
        opis="Vprašajte v svojih besedah – o poslovanju ali o tem, kaj asistent zna."
        pomoc="asistent"
        dejanja={
          <Button leftSection={<IconPlus size={18} />} component={Link} to="/asistent" variant="light">
            Nov pogovor
          </Button>
        }
      />
      <Grid gap="md">
        <Grid.Col span={{ base: 12, md: 4, lg: 3 }} visibleFrom="md">
          <Paper radius="lg" shadow="xs" p="xs">
            <Text size="xs" fw={700} tt="uppercase" c="dimmed" px="xs" py={4}>
              Pogovori
            </Text>
            <ScrollArea.Autosize mah="calc(100dvh - 260px)" type="auto">
              {seznam.length === 0 && (
                <Text size="sm" c="dimmed" px="xs" py="sm">
                  {pogovori.isLoading ? 'Nalagam …' : 'Še nimate pogovorov.'}
                </Text>
              )}
              {seznam.map((p) => (
                <Group key={p.id} gap={0} wrap="nowrap" align="center">
                  <NavLink
                    component={Link}
                    to={`/asistent/${p.id}`}
                    active={p.id === pogovorId}
                    label={p.naslov}
                    description={preteklo(p.zadnjeSporocilo)}
                    styles={{ label: { overflowWrap: 'anywhere' } }}
                    style={{ flex: 1, minWidth: 0 }}
                  />
                  <Tooltip label="Izbriši pogovor">
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      size="sm"
                      aria-label={`Izbriši pogovor ${p.naslov ?? ''}`}
                      onClick={() => setIzbris(p)}
                    >
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              ))}
            </ScrollArea.Autosize>
          </Paper>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 8, lg: 9 }}>
          <Stack gap="xs">
            {seznam.length > 0 && (
              <Group gap="xs" hiddenFrom="md" wrap="nowrap">
                <Select
                  style={{ flex: 1 }}
                  aria-label="Pogovor"
                  placeholder="Nov pogovor"
                  data={seznam.map((p) => ({ value: String(p.id), label: p.naslov ?? '' }))}
                  value={pogovorId === null ? null : String(pogovorId)}
                  onChange={(v) => void navigate(v ? `/asistent/${v}` : '/asistent')}
                  allowDeselect={false}
                />
                {pogovorId !== null && (
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    size="lg"
                    aria-label="Izbriši pogovor"
                    onClick={() => setIzbris(seznam.find((p) => p.id === pogovorId) ?? null)}
                  >
                    <IconTrash size={18} />
                  </ActionIcon>
                )}
              </Group>
            )}
            <Paper
              radius="lg"
              shadow="xs"
              p="sm"
              h={{ base: 'calc(100dvh - 340px)', md: 'calc(100dvh - 230px)' }}
              mih={360}
            >
              <PogovorAsistenta
                key={pogovorId ?? 'nov'}
                pogovorId={pogovorId}
                naPogovor={(nov) => void navigate(`/asistent/${nov}`, { replace: true })}
              />
            </Paper>
          </Stack>
        </Grid.Col>
      </Grid>
      <IzbrisPogovora
        pogovor={izbris}
        zapri={() => setIzbris(null)}
        izbrisano={(p) => {
          setIzbris(null);
          if (p.id === pogovorId) {
            void navigate('/asistent');
          }
        }}
      />
    </Box>
  );
}

function IzbrisPogovora({
  pogovor,
  zapri,
  izbrisano,
}: {
  pogovor: Pogovor | null;
  zapri: () => void;
  izbrisano: (p: Pogovor) => void;
}) {
  const odjemalec = useQueryClient();
  const [dela, setDela] = useState(false);
  const [napaka, setNapaka] = useState<string | null>(null);
  const izbrisi = async () => {
    if (!pogovor) {
      return;
    }
    setDela(true);
    setNapaka(null);
    try {
      await izvedi(api.DELETE('/api/asistent/pogovori/{id}', { params: { path: { id: pogovor.id ?? 0 } } }));
      odjemalec.removeQueries({ queryKey: [...KLJUC_ASISTENT, 'pogovor', pogovor.id] });
      await odjemalec.invalidateQueries({ queryKey: [...KLJUC_ASISTENT, 'pogovori'] });
      izbrisano(pogovor);
    } catch (e) {
      setNapaka(sporocilo(e));
    } finally {
      setDela(false);
    }
  };
  return (
    <Modal opened={pogovor !== null} onClose={zapri} title="Izbriši pogovor" radius="lg">
      <Stack gap="sm">
        <Text size="sm">
          Pogovor »{pogovor?.naslov}« bo izbrisan. Tega ni mogoče razveljaviti. Vprašanja, ki ste jih poslali
          razvijalcem, ostanejo v Povratnih informacijah.
        </Text>
        {napaka && (
          <Text size="sm" c="red" role="alert">
            {napaka}
          </Text>
        )}
        <Group justify="flex-end" gap="xs">
          <Button variant="subtle" onClick={zapri}>
            Prekliči
          </Button>
          <Button color="red" loading={dela} onClick={() => void izbrisi()}>
            Izbriši
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
